import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { auth, db } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useLearningProfileStore } from "../../lib/stores/learningProfileStore";
import {
    AGE_GROUP_OPTIONS,
    COGNITIVE_LEVEL_OPTIONS,
    getCurriculumApproach,
    getQuestionsForApproach,
    getCurriculumApproachDisplay
} from "../../lib/soloAssessmentLogic";
import { Icons } from "../../lib/Icons";

const LearningProfileBuilder = () => {
    const navigate = useNavigate();
    const { profileId } = useParams();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const {
        step,
        profileAnswers,
        questionnaireAnswers,
        currentQuestionIndex,
        curriculumApproach,
        setProfileAnswer,
        setQuestionnaireAnswer,
        proceedToQuestions,
        nextQuestion,
        prevQuestion,
        goToQuestion,
        setStep,
        reset,
        loadProfile,
        isProfileComplete,
        getCurrentQuestions,
        getProgress
    } = useLearningProfileStore();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (!currentUser) {
                navigate('/login');
                return;
            }
            setUser(currentUser);

            // If editing an existing profile, load it
            if (profileId && profileId !== 'new') {
                try {
                    const profileDoc = await getDoc(doc(db, 'learningProfiles', profileId));
                    if (profileDoc.exists()) {
                        loadProfile({ id: profileDoc.id, ...profileDoc.data() });
                    } else {
                        navigate('/solo/profiles');
                    }
                } catch (err) {
                    console.error('Error loading profile:', err);
                    navigate('/solo/profiles');
                }
            } else {
                // New profile - reset store
                reset();
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, [navigate, profileId, loadProfile, reset]);

    const handleProfileComplete = () => {
        if (isProfileComplete()) {
            proceedToQuestions();
        }
    };

    const handleQuestionAnswer = (value) => {
        const questions = getCurrentQuestions();
        const currentQuestion = questions[currentQuestionIndex];
        if (currentQuestion) {
            setQuestionnaireAnswer(currentQuestion.id, value);
        }
    };

    const handleQuestionnaireComplete = () => {
        // Navigate to results - we'll generate them there
        navigate('/solo/profiles/results', { state: { fromBuilder: true } });
    };

    const questions = getCurrentQuestions();
    const currentQuestion = questions[currentQuestionIndex];
    const currentAnswer = currentQuestion ? questionnaireAnswers[currentQuestion.id] : null;
    const progress = getProgress();

    const isLastQuestion = currentQuestionIndex === questions.length - 1;
    const allQuestionsAnswered = questions.every(q => questionnaireAnswers[q.id] !== undefined);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500/20 border-t-purple-500"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-900">
            {/* Header */}
            <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/solo/profiles" className="flex items-center gap-3">
                            <img
                                src="/brand/White logo on purple.png"
                                alt="SEN-Ed"
                                className="w-10 h-10 rounded-xl"
                            />
                            <span className="text-xl font-bold text-white">Learning Profiles</span>
                        </Link>

                        <button
                            onClick={() => navigate('/solo/profiles')}
                            className="text-slate-400 hover:text-white transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </header>

            {/* Progress Bar */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-400">
                        Step {progress.current} of {progress.total}: {progress.label}
                    </span>
                    {step === 'questions' && progress.questionProgress && (
                        <span className="text-sm text-purple-400">
                            Question {progress.questionProgress.current} of {progress.questionProgress.total}
                        </span>
                    )}
                </div>
                <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all duration-300"
                        style={{
                            width: step === 'profile'
                                ? '33%'
                                : step === 'questions'
                                    ? `${33 + (currentQuestionIndex / questions.length) * 34}%`
                                    : '100%'
                        }}
                    />
                </div>
            </div>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Step 1: Basic Profile */}
                {step === 'profile' && (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <h1 className="text-3xl font-bold text-white mb-3">
                                Build a Learning Profile
                            </h1>
                            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                                Answer a few questions to get personalized activity recommendations for supporting this student's learning.
                            </p>
                        </div>

                        {/* Age Group Selection */}
                        <div className="bg-slate-800 rounded-2xl p-6">
                            <h2 className="text-xl font-semibold text-white mb-2">
                                What age group is this student?
                            </h2>
                            <p className="text-slate-400 mb-6">
                                Select the key stage or phase that best matches their current placement.
                            </p>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {AGE_GROUP_OPTIONS.map(option => (
                                    <button
                                        key={option.id}
                                        onClick={() => setProfileAnswer('ageGroup', option.id)}
                                        className={`p-4 rounded-xl text-left transition-all ${profileAnswers.ageGroup === option.id
                                                ? 'bg-purple-500/20 border-2 border-purple-500 ring-2 ring-purple-500/20'
                                                : 'bg-slate-700/50 border-2 border-transparent hover:bg-slate-700 hover:border-slate-600'
                                            }`}
                                    >
                                        <div className="font-medium text-white">{option.label}</div>
                                        <div className="text-sm text-slate-400 mt-1">{option.description}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Cognitive Level Selection */}
                        <div className="bg-slate-800 rounded-2xl p-6">
                            <h2 className="text-xl font-semibold text-white mb-2">
                                What is their cognitive/learning level?
                            </h2>
                            <p className="text-slate-400 mb-6">
                                This helps us recommend activities at the right developmental level.
                            </p>

                            <div className="space-y-3">
                                {COGNITIVE_LEVEL_OPTIONS.map(option => (
                                    <button
                                        key={option.id}
                                        onClick={() => setProfileAnswer('cognitiveLevel', option.id)}
                                        className={`w-full p-4 rounded-xl text-left transition-all ${profileAnswers.cognitiveLevel === option.id
                                                ? 'bg-purple-500/20 border-2 border-purple-500 ring-2 ring-purple-500/20'
                                                : 'bg-slate-700/50 border-2 border-transparent hover:bg-slate-700 hover:border-slate-600'
                                            }`}
                                    >
                                        <div className="font-medium text-white">{option.label}</div>
                                        <div className="text-sm text-slate-400 mt-1">{option.description}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Curriculum Approach Preview */}
                        {profileAnswers.cognitiveLevel && (
                            <div className={`rounded-2xl p-6 ${getCurriculumApproach(profileAnswers) === 'ENGAGEMENT_MODEL'
                                    ? 'bg-purple-500/10 border border-purple-500/30'
                                    : 'bg-blue-500/10 border border-blue-500/30'
                                }`}>
                                {(() => {
                                    const approach = getCurriculumApproach(profileAnswers);
                                    const display = getCurriculumApproachDisplay(approach);
                                    return (
                                        <>
                                            <div className="flex items-center gap-3 mb-2">
                                                <Icons.Sparkles className={`w-5 h-5 ${approach === 'ENGAGEMENT_MODEL' ? 'text-purple-400' : 'text-blue-400'}`} />
                                                <h3 className={`font-semibold ${approach === 'ENGAGEMENT_MODEL' ? 'text-purple-300' : 'text-blue-300'}`}>
                                                    {display.title}
                                                </h3>
                                            </div>
                                            <p className="text-slate-300 text-sm">{display.description}</p>
                                        </>
                                    );
                                })()}
                            </div>
                        )}

                        {/* Continue Button */}
                        <div className="flex justify-end">
                            <button
                                onClick={handleProfileComplete}
                                disabled={!isProfileComplete()}
                                className={`px-8 py-3 rounded-xl font-medium transition-all ${isProfileComplete()
                                        ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600'
                                        : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                    }`}
                            >
                                Continue to Questions →
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2: Questionnaire */}
                {step === 'questions' && currentQuestion && (
                    <div className="space-y-8">
                        {/* Question Card */}
                        <div className="bg-slate-800 rounded-2xl p-8">
                            {/* Domain Badge */}
                            <div className="flex items-center gap-2 mb-6">
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${currentQuestion.domain === 'Communication' ? 'bg-blue-500/20 text-blue-300' :
                                        currentQuestion.domain === 'Cognition' ? 'bg-green-500/20 text-green-300' :
                                            currentQuestion.domain === 'SEMH' ? 'bg-yellow-500/20 text-yellow-300' :
                                                'bg-purple-500/20 text-purple-300'
                                    }`}>
                                    {currentQuestion.domain}
                                </span>
                            </div>

                            <h2 className="text-2xl font-semibold text-white mb-8">
                                {currentQuestion.text}
                            </h2>

                            <div className="space-y-3">
                                {currentQuestion.options.map(option => (
                                    <button
                                        key={option.value}
                                        onClick={() => handleQuestionAnswer(option.value)}
                                        className={`w-full p-4 rounded-xl text-left transition-all ${currentAnswer === option.value
                                                ? 'bg-purple-500/20 border-2 border-purple-500 ring-2 ring-purple-500/20'
                                                : 'bg-slate-700/50 border-2 border-transparent hover:bg-slate-700 hover:border-slate-600'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${currentAnswer === option.value
                                                    ? 'border-purple-500 bg-purple-500'
                                                    : 'border-slate-500'
                                                }`}>
                                                {currentAnswer === option.value && (
                                                    <Icons.Check className="w-3 h-3 text-white" />
                                                )}
                                            </div>
                                            <span className="text-white">{option.label}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Question Navigation Dots */}
                        <div className="flex justify-center gap-2">
                            {questions.map((q, idx) => (
                                <button
                                    key={q.id}
                                    onClick={() => goToQuestion(idx)}
                                    className={`w-3 h-3 rounded-full transition-all ${idx === currentQuestionIndex
                                            ? 'bg-purple-500 scale-125'
                                            : questionnaireAnswers[q.id]
                                                ? 'bg-purple-500/50 hover:bg-purple-500/70'
                                                : 'bg-slate-600 hover:bg-slate-500'
                                        }`}
                                    title={`Question ${idx + 1}`}
                                />
                            ))}
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex justify-between">
                            <button
                                onClick={() => currentQuestionIndex === 0 ? setStep('profile') : prevQuestion()}
                                className="px-6 py-3 rounded-xl font-medium bg-slate-700 text-white hover:bg-slate-600 transition-colors"
                            >
                                ← Back
                            </button>

                            {isLastQuestion ? (
                                <button
                                    onClick={handleQuestionnaireComplete}
                                    disabled={!allQuestionsAnswered}
                                    className={`px-8 py-3 rounded-xl font-medium transition-all ${allQuestionsAnswered
                                            ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600'
                                            : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                        }`}
                                >
                                    Generate Recommendations →
                                </button>
                            ) : (
                                <button
                                    onClick={nextQuestion}
                                    disabled={!currentAnswer}
                                    className={`px-8 py-3 rounded-xl font-medium transition-all ${currentAnswer
                                            ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600'
                                            : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                        }`}
                                >
                                    Next →
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default LearningProfileBuilder;
